import React, { useState, useContext, useEffect } from "react";
import {
  Form,
  ProgressBar,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Menubar from "../Menubar";
import BackgroundFooter from "../BackgroundFooter";
import NavigationBar from "../Nav";
import { ScoreContext } from "../ScoreContext";
import generateAppSecCertificate from "./generateAppSecCertificate";
import susanLogo from "../../Assets/srql.png";
import html2canvas from "html2canvas";
import AppSecCertificateCanvas from "./AppSecCertificateCanvas";

// ✅ AppSec QUESTIONS
const appSecQuestions = [
  {
    questionId: 1,
    type: "single",
   
   
 
    question:
      "A web application firewall (WAF) is deployed in front of an application. Which of the following risks can the WAF MOST reliably mitigate?",
    options: [
      { value: "A", label: "Common SQL injection patterns with known signatures" },
      { value: "B", label: "Authorization logic flaws in application code" },
      { value: "C", label: "Unauthorized access to another user's data via ID manipulation" },
      { value: "D", label: "Framework-level deserialization vulnerabilities" },
    ],
    correctAnswer: "A",
    description:
      "WAFs are effective against known attack signatures but cannot fix application logic flaws.",
    recommendations:
      "Use WAF as a defense-in-depth control alongside secure coding practices.",
  },
  {
    questionId: 2,
    type: "single",
  
   
    question:
      "A user modifies a numeric ID in a URL and is able to view another user's data. What type of vulnerability does this represent?",
    options: [
      { value: "A", label: "Cross-site scripting (XSS)" },
      { value: "B", label: "Broken Object Level Authorization (IDOR/BOLA)" },
      { value: "C", label: "SQL Injection" },
      { value: "D", label: "Cross-site request forgery (CSRF)" },
    ],
    correctAnswer: "B",
    description:
      "This is a classic broken access control issue where authorization checks are missing.",
    recommendations:
      "Implement strict object-level authorization checks on every request.",
  },
  {
    questionId: 3,
    type: "multi",
 
    question:
      "Which of the following should never be present in source code or frontend assets? (Select all that apply)",
    options: [
      { value: "A", label: "API keys and secret tokens" },
      { value: "B", label: "Database connection strings with passwords" },
      { value: "C", label: "Internal admin/service email identifiers" },
      { value: "D", label: "Public CSS class names" },
      { value: "E", label: "Private encryption keys" },
      { value: "F", label: "Test or development credentials" },
    ],
    correctAnswer: ["A", "B", "C", "E", "F"],
    description:
      "Sensitive data must never be exposed in code or frontend assets.",
    recommendations:
      "Use secrets managers and environment variables. Rotate exposed secrets immediately.",
  },
  {
    questionId: 4,
    type: "single",
    
    
    question:
      "What is the correct method for handling database credentials required at runtime?",
    options: [
      { value: "A", label: "Hardcode credentials in a configuration file" },
      { value: "B", label: "Encode credentials (e.g., base64) and store in code" },
      { value: "C", label: "Use environment variables or a secrets manager" },
      { value: "D", label: "Hardcode credentials with restricted access comments" },
    ],
    correctAnswer: "C",
    description:
      "Secure storage of credentials is critical to prevent unauthorized access.",
    recommendations:
      "Always use centralized secrets management solutions.",
  },
  {
    questionId: 5,
    type: "multi",
   
    
    question:
      "An application includes a hidden HTML field containing an internal admin email address visible in page source. What are the risks? (Select all that apply)",
    options: [
      { value: "A", label: "Exposure of internal account identifiers" },
      { value: "B", label: "Acceptable since the field is hidden" },
      { value: "C", label: "Risk if the account belongs to an individual who leaves" },
      { value: "D", label: "Enables reconnaissance for targeted attacks" },
    ],
    correctAnswer: ["A", "C", "D"],
    description:
      "Hidden fields are visible in source and can expose sensitive internal data.",
    recommendations:
      "Avoid exposing internal identifiers in frontend code.",
  },
  {
    questionId: 6,
    type: "single",
  

    question:
      "A password policy is enforced using only client-side JavaScript validation. Is this sufficient?",
    options: [
      { value: "A", label: "Yes, users cannot bypass JavaScript validation" },
      { value: "B", label: "No, server-side validation is also required" },
      { value: "C", label: "Yes, if HTTPS is enabled" },
      { value: "D", label: "No, client-side validation should be removed entirely" },
    ],
    correctAnswer: "B",
    description:
      "Client-side validation alone is insufficient for enforcing security rules.",
    recommendations:
      "Always validate inputs on the server side.",
  },
  {
    questionId: 7,
    type: "single",
   
   
    question:
      "A critical vulnerability is identified in a framework used by your application, with a non-breaking patch available. What should be done?",
    options: [
      { value: "A", label: "Delay patching until the next release cycle" },
      { value: "B", label: "Apply the patch promptly after testing" },
      { value: "C", label: "Assume the security team will handle it" },
      { value: "D", label: "Mitigate using WAF rules only" },
    ],
    correctAnswer: "B",
    description:
      "Timely patching is critical to reduce exposure to known vulnerabilities.",
    recommendations:
      "Apply patches promptly and do not rely solely on compensating controls.",
  },
  {
    questionId: 8,
    type: "single",
  
   
    question:
      "An API returns additional user data (e.g., mobile number) not required by the interface. Should this be included?",
    options: [
      { value: "A", label: "Yes, because the data belongs to the user" },
      { value: "B", label: "Yes, for potential future use" },
      { value: "C", label: "No, only required data should be returned" },
      { value: "D", label: "Only if user consent is obtained" },
    ],
    correctAnswer: "C",
    description:
      "Data minimization reduces risk of exposure.",
    recommendations:
      "Return only necessary data from APIs.",
  },
  {
    questionId: 9,
    type: "single",
   
    
    question:
      "A developer commits an API key to a repository and removes it in a later commit. Is the key still considered exposed?",
    options: [
      { value: "A", label: "No, it was removed in the next commit" },
      { value: "B", label: "Yes, it remains in version history" },
      { value: "C", label: "No, if the repository is private" },
      { value: "D", label: "Only if someone accessed it before removal" },
    ],
    correctAnswer: "B",
    description:
      "Secrets remain in Git history even after deletion.",
    recommendations:
      "Rotate keys immediately and clean repository history.",
  },
  {
    questionId: 10,
    type: "multi",
   
    
    question:
      "Which of the following actions should be completed before merging code that handles sensitive functionality? (Select all that apply)",
    options: [
      { value: "A", label: "Perform threat modeling" },
      { value: "B", label: "Validate inputs on the server side" },
      { value: "C", label: "Run dependency and secrets scanning" },
      { value: "D", label: "Rely on middleware authorization without validating business logic" },
      { value: "E", label: "Request a security review" },
    ],
    correctAnswer: ["A", "B", "C", "E"],
    description:
      "Security must be integrated into the development lifecycle.",
    recommendations:
      "Perform reviews, validation, and scanning before merging sensitive code.",
  },
];

const btnPrimary = {
  padding: "7px 16px",
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
  border: "none",
  backgroundColor: "#024A82",
  color: "#FFFFFF",
  fontFamily: "'Inter', sans-serif",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
};

// ========== BACKEND ENDPOINT FUNCTIONS ==========
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

const fetchUserProfile = async (username) => {
  try {
    const response = await fetch(`${API_BASE}/users/${username}/profile`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    });
    return await response.json();
  } catch (error) {
    console.error("Profile fetch failed:", error);
    return { name: username || "Participant", email: "" };
  }
};

const saveAllResponsesToBackend = async (username, section, responses) => {
  try {
    const score = responses.reduce((acc, r) => acc + (r.isCorrect ? 1 : 0), 0);
    const percentage = Math.round((score / 10) * 100);
    await fetch(`${API_BASE}/assessments/${username}/${section}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ responses, score, percentage }),
    });
    console.log("✅ Assessment saved:", { username, percentage });
  } catch (error) {
    console.error("Save failed:", error);
  }
};

const fetchResponsesForSection = async (username, section) => {
  try {
    const response = await fetch(
      `${API_BASE}/assessments/${username}/${section}`,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    const data = await response.json();
    return data.responses || [];
  } catch (error) {
    console.error("Fetch failed:", error);
    return [];
  }
};

const clearResponsesForSection = async (username, section) => {
  try {
    await fetch(`${API_BASE}/assessments/${username}/${section}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
  } catch (error) {
    console.error("Clear failed:", error);
  }
};

// Function to download the PDF guide (placeholder – update URL when ready)
const AppSecPdf = "https://d2i158pkl9gun1.cloudfront.net/APPSECURITY_CHECKLIST.pdf"; // TODO: Add AppSec Training Guide PDF URL here

const handleDownloadPDFGuide = () => {
  if (!AppSecPdf) {
    alert("PDF guide coming soon!");
    return;
  }
  const link = document.createElement("a");
  link.href = AppSecPdf;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ✅ Simple shuffle function
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};


const AppSecAssessmentModule = ({ userData }) => {
  const {
    fetchResponsesForSection: ctxFetch,
    updateResponses,
    saveAllResponsesToBackend: ctxSave,
    updateScore,
    clearResponsesForSection: ctxClear,
  } = useContext(ScoreContext);
  const navigate = useNavigate();
  const section = "AppSecAssessment";

  const SIDEBAR_EXPANDED_WIDTH = 317;
  const SIDEBAR_COLLAPSED_WIDTH = 90;

  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false
  );
  const [currentStep, setCurrentStep] = useState(0); // 0=intro, 1=questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState("desc");
  const [currentSlide, setCurrentSlide] = useState(0);

  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState(null);
  const [showCertificateCanvas, setShowCertificateCanvas] = useState(false);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);

  const handleSidebarToggle = () => setIsSidebarCollapsed((prev) => !prev);

  useEffect(() => {
    localStorage.setItem(
      "isSidebarCollapsed",
      JSON.stringify(isSidebarCollapsed)
    );
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (userData?.cognito?.username) {
      ctxClear(section);
      ctxFetch(userData.cognito.username, section);
    }
  }, [userData, ctxFetch, ctxClear]);

  const handleStartAssessment = () => {
    setShowRulesModal(true);
  };

  const handleBackToIntro = () => {
    setShowResults(false);
    setCurrentStep(0);
    setCurrentSlide(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
  };

  // Certificate preview
  const handleDownloadCertificate = () => {
    const previewUrl = generateAppSecCertificate(
      userData,
      { percentage, score, totalQuestions },
      susanLogo,
      true // previewMode
    );
    setCertificatePreviewUrl(previewUrl);
    setShowCertificateModal(true);
  };

  const handleActualDownload = () => {
    generateAppSecCertificate(
      userData,
      { percentage, score, totalQuestions },
      susanLogo,
      false,
      "pdf"
    );
  };

  const handleShareLinkedIn = () => {
    setShowCertificateModal(false);
    setShowLinkedInModal(true);
  };

  const downloadCertificateAsPNG = async () => {
    try {
      console.log("🎨 Starting PNG certificate generation...");
      setShowCertificateCanvas(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = document.getElementById("appsec-certificate-canvas");
      if (!canvas) throw new Error("Certificate canvas not found");

      const generatedCanvas = await html2canvas(canvas, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#f8fcf8",
        logging: false,
      });

      generatedCanvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const userName =
              userData?.name ||
              userData?.["cognito:username"] ||
              "Participant";
            const currentDate = new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            });
            link.href = url;
            link.download = `AppSec_Certificate_${userName.replace(
              /\s+/g,
              "_"
            )}_${currentDate.replace(/\s+/g, "_")}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log("✅ PNG Certificate downloaded successfully!");
            setShowCertificateCanvas(false);
          }
        },
        "image/png",
        1.0
      );
    } catch (error) {
      console.error("❌ PNG generation failed:", error);
      alert("Failed to generate PNG certificate. Please try again.");
      setShowCertificateCanvas(false);
    }
  };

  // ===== MULTI-SELECT HANDLER =====
  const handleMultiAnswerSelect = (questionIndex, optionValue) => {
    const q = shuffledQuestions[questionIndex];
    setSelectedAnswers((prev) => {
      const current = prev[questionIndex]
        ? Array.isArray(prev[questionIndex])
          ? prev[questionIndex]
          : [prev[questionIndex]]
        : [];
      const updated = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];

      const correctArr = Array.isArray(q.correctAnswer)
        ? [...q.correctAnswer].sort()
        : [q.correctAnswer];
      const selectedArr = [...updated].sort();
      const isCorrect =
        JSON.stringify(correctArr) === JSON.stringify(selectedArr);
      const value = isCorrect ? 1 : 0;

      updateResponses(section, q.questionId, {
        questionId: q.questionId,
        answer: updated,
        isCorrect,
        questionText: q.question,
      });
      updateScore(section, q.questionId, value, q.question, q.correctAnswer);
      if (userData?.cognito?.username) {
        ctxSave(userData.cognito.username, section);
      }
      return { ...prev, [questionIndex]: updated };
    });
  };

  // ===== SINGLE-SELECT HANDLER =====
  const handleAnswerSelect = (answer) => {
    const q = shuffledQuestions[currentQuestionIndex];
    const isCorrect = answer === q.correctAnswer;
    const value = isCorrect ? 1 : 0;

    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: answer,
    }));

    updateResponses(section, q.questionId, {
      questionId: q.questionId,
      answer,
      isCorrect,
      questionText: q.question,
    });

    updateScore(section, q.questionId, value, q.question, q.correctAnswer);
    if (userData?.cognito?.username) {
      ctxSave(userData.cognito.username, section);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((i) => i - 1);
    }
  };

  const handlePrevSlide = () => {
    if (currentSlide > 1) {
      setCurrentSlide((prev) => prev - 1);
    } else if (currentSlide === 1) {
      setCurrentSlide(0);
    }
  };

  const sidebarWidth = isSidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;
  const totalQuestions = shuffledQuestions.length;
  const answeredQuestions = Object.keys(selectedAnswers).length;
  const progress = totalQuestions
    ? Math.round((answeredQuestions / totalQuestions) * 100)
    : 0;

  const score = Object.entries(selectedAnswers).reduce((acc, [idx, ans]) => {
    const q = shuffledQuestions[Number(idx)];
    if (!q) return acc;
    if (Array.isArray(q.correctAnswer)) {
      const correctArr = [...q.correctAnswer].sort();
      const selectedArr = Array.isArray(ans) ? [...ans].sort() : [ans];
      return (
        acc +
        (JSON.stringify(correctArr) === JSON.stringify(selectedArr) ? 1 : 0)
      );
    }
    return acc + (String(ans) === String(q.correctAnswer) ? 1 : 0);
  }, 0);

  const percentage = totalQuestions
    ? Math.round((score / totalQuestions) * 100)
    : 0;
  const currentQuestion = shuffledQuestions[currentQuestionIndex];

  // Placeholder video URL – replace when available
  const videoUrl = "https://d23ngkvdyboms1.cloudfront.net/Application Security.mov"; // TODO: Add AppSec Training Video URL here

  return (
    <div>
      <Row className="g-0">
        {/* Sidebar */}
        <Col
          xs={12}
          className={`bg-transparent sidebar-container ${
            isSidebarCollapsed ? "collapsed" : "hello"
          }`}
          style={{
            width: isSidebarCollapsed ? "90px" : "317px",
            transition: "width 0.3s ease",
          }}
        >
          <Menubar
            isCollapsed={isSidebarCollapsed}
            onToggle={handleSidebarToggle}
          />
        </Col>

        {/* Main content */}
        <Col
          className="pt-0 px-0 ps-0 section-container"
          style={{
            width: `calc(100% - ${sidebarWidth}px)`,
            transition: "width 0.3s ease",
          }}
        >
          <NavigationBar
            userData={userData}
            isSidebarCollapsed={isSidebarCollapsed}
          />

          {/* Progress bar */}
          {currentStep !== 0 && (
            <div
              className="sticky-top bg-white"
              style={{ top: "50px", zIndex: 999 }}
            >
              <div style={{ position: "relative" }}>
                <ProgressBar
                  now={progress}
                  className="custom-progress-bar"
                  style={{ height: "20px", margin: "0 10px 10px 10px" }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#000",
                  }}
                >
                  {progress}% completed
                </span>
              </div>
            </div>
          )}

          <Col className="overflow-auto p-4">
            {/* INTRO SCREEN */}
            {currentStep === 0 && (
              <Row className="justify-content-center">
                <Col xs={12}>
                  <Card className="iam-card iam-card-intro text-center">
                    <Card.Body>
                      <h2>Application Security Fundamentals – Q&A Module</h2>
                      <p>
                        Understand Application Security Fundamentals through
                        training and a 10-question assessment.
                      </p>

                      {/* VIDEO SCREEN */}
                      {currentSlide === 0 && (
                        <div>
                          <div className="mb-4">
                            {videoUrl ? (
                              <video
                                src={videoUrl}
                                controls
                                controlsList="nodownload"
                                onContextMenu={(e) => e.preventDefault()}
                                style={{ width: "68%" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "420px",
                                  borderRadius: "10px",
                                  border: "2px dashed #999",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "#f8f9fa",
                                  flexDirection: "column",
                                  gap: "1rem",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "1.2rem",
                                    color: "#666",
                                  }}
                                >
                                  Application Security Training Video
                                </div>
                                <div
                                  style={{
                                    color: "#999",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Video coming soon – add video source URL here
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="d-flex justify-content-center gap-3 mt-3">
                            <Button
                              variant=""
                              onClick={handleStartAssessment}
                              style={{ height: "50px" }}
                              className="bg-secondary text-white"
                            >
                              Skip Video
                            </Button>

                            <div
                              className="d-flex justify-content-center"
                              style={{ marginTop: "-15px" }}
                            >
                              <Button
                                variant="primary"
                                onClick={handleStartAssessment}
                                style={{
                                  height: "50px",
                                  marginTop: "15px",
                                  ...btnPrimary,
                                }}
                              >
                                Done - Start Assessment
                              </Button>

                              <Button
                                variant="success"
                                onClick={handleDownloadPDFGuide}
                                style={{
                                  height: "50px",
                                  marginTop: "15px",
                                  marginLeft: "15px",
                                }}
                              >
                                Download PDF Guide
                              </Button>

                              {/* Rules Confirmation Modal */}
                              <Modal
                                show={showRulesModal}
                                onHide={() => setShowRulesModal(false)}
                                centered
                                size="lg"
                              >
                                <Modal.Header
                                  closeButton
                                  style={{ borderBottom: "none" }}
                                >
                                  <Modal.Title>
                                    Application Security Assessment Rules
                                  </Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                  <ul>
                                    <li>
                                      10 questions on Application Security
                                     
                                    </li>
                                    <li>
                                      Mix of single-choice and multi-select
                                      questions
                                    </li>
                                    <li>
                                      1 point per correct answer (no negative
                                      marking)
                                    </li>
                                    <li>Certificate on 75%+ score</li>
                                  </ul>

                                  <div
                                    style={{
                                      marginTop: "20px",
                                      padding: "15px",
                                      backgroundColor: "#f8f9fa",
                                      borderRadius: "8px",
                                      border: "1px solid #dee2e6",
                                    }}
                                  >
                                    <h6
                                      style={{
                                        fontWeight: "bold",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      Certificate Validity:
                                    </h6>
                                    <p
                                      style={{
                                        fontSize: "14px",
                                        lineHeight: "1.6",
                                        marginBottom: 0,
                                      }}
                                    >
                                      This certification is based on current application security best
                                      practices. Certificate holders may be
                                      required to undergo revision or
                                      re-certification when major OWASP updates
                                      are released. The certificate reflects
                                      demonstrated knowledge of practical
                                      application security principles and may be
                                      shared on LinkedIn, included in your CV, or
                                      displayed in your workplace.
                                    </p>
                                  </div>

                                  <div
                                    style={{
                                      backgroundColor: "#fff3cd",
                                      padding: "12px",
                                      borderRadius: "5px",
                                      marginTop: "20px",
                                      border: "1px solid #ffc107",
                                    }}
                                  >
                                    <strong style={{ color: "#856404" }}>
                                      Important:
                                    </strong>
                                    <span
                                      style={{
                                        color: "#856404",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      Attempt honestly. No retakes without
                                      clearing responses.
                                    </span>
                                  </div>
                                </Modal.Body>
                                <Modal.Footer
                                  style={{
                                    borderTop: "none",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Button
                                    variant="secondary"
                                    onClick={() => setShowRulesModal(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="success"
                                    onClick={() => {
                                      setShowRulesModal(false);
                                      const shuffled =
                                        shuffleArray(appSecQuestions);
                                      setShuffledQuestions(shuffled);
                                      setCurrentStep(1);
                                    }}
                                  >
                                    I Agree & Start Exam
                                  </Button>
                                </Modal.Footer>
                              </Modal>
                            </div>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* QUESTIONS & RESULTS */}
            {currentStep !== 0 && (
              <Row className="justify-content-center">
                <Col xs={12}>
                  <h4 className="mb-3">
                    Application Security Fundamentals – Exam Practice
                  </h4>

                  {!showResults && (
                    <div className="result-action1">
                      <Button
                        variant="primary"
                        onClick={handleBackToIntro}
                        style={btnPrimary}
                      >
                        Back to Training
                      </Button>
                    </div>
                  )}

                  <Card className="iam-card">
                    <Card.Body>
                      {!showResults && (
                        <div className="assessment-header-new mb-4">
                          <div className="question-chip-inline">
                            Question{" "}
                            <strong>{currentQuestionIndex + 1}</strong> of{" "}
                            <strong>{totalQuestions}</strong>
                          </div>
                          <div className="answered-badge-inline">
                            Answered {answeredQuestions}/{totalQuestions}
                          </div>
                        </div>
                      )}

                      {!showResults && currentQuestion && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12} lg={8}>
                              <div className="scenario-box">
                                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                  <span className="scenario-label" style={{ marginBottom: 0 }}>Question</span>
                                  {currentQuestion.type === "multi" && (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#024A82",
                                        background: "#eef4fb",
                                        padding: "3px 12px",
                                        borderRadius: "20px",
                                        border: "1px solid #c5d9f0",
                                        boxShadow: "0 1px 3px rgba(2,74,130,0.08)",
                                        letterSpacing: "0.3px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      Multi-Select
                                    </span>
                                  )}
                                </div>
                                <h5 className="scenario-title">
                                  {currentQuestion.question}
                                </h5>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mb-4">
                            {currentQuestion.options.map((opt) => {
                              const currentAnswer =
                                selectedAnswers[currentQuestionIndex];
                              const isSelected =
                                currentQuestion.type === "multi"
                                  ? Array.isArray(currentAnswer) &&
                                    currentAnswer.includes(opt.value)
                                  : currentAnswer === opt.value;

                              return (
                                <Col
                                  xs={12}
                                  key={opt.value}
                                  className="mb-3"
                                >
                                  <div
                                    className={`option-tile ${
                                      isSelected ? "active" : ""
                                    }`}
                                    onClick={() => {
                                      if (currentQuestion.type === "multi") {
                                        handleMultiAnswerSelect(
                                          currentQuestionIndex,
                                          opt.value
                                        );
                                      } else {
                                        handleAnswerSelect(opt.value);
                                      }
                                    }}
                                  >
                                    <div className="option-key">{opt.value}</div>
                                    <div className="option-content">
                                      {opt.label}
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>

                
                         

                          <Row className="mt-4">
                            <Col xs={6}>
                              <Button
                                variant="outline-secondary"
                                onClick={handlePrevious}
                                disabled={currentQuestionIndex === 0}
                              >
                                Previous
                              </Button>
                            </Col>
                            <Col xs={6} className="text-end">
                              <Button
                                variant="primary"
                                onClick={handleNext}
                                style={btnPrimary}
                              >
                                {currentQuestionIndex === totalQuestions - 1
                                  ? "Finish Assessment"
                                  : "Next Question"}
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* RESULTS */}
                      <div className="result-iam-card">
                        {showResults && (
                          <Col xs={12}>
                            <div
                              style={{
                                background:
                                  "linear-gradient(135deg, #035EA5 0%, #024080 50%)",
                                borderRadius: "20px",
                                padding: "30px",
                                color: "white",
                                boxShadow:
                                  "0 25px 50px rgba(30, 58, 138, 0.4)",
                              }}
                            >
                              {/* Header */}
                              <div
                                style={{
                                  textAlign: "center",
                                  marginBottom: "30px",
                                }}
                              >
                                <h1
                                  style={{
                                    fontSize: "2.5rem",
                                    fontWeight: "bold",
                                    marginBottom: "10px",
                                  }}
                                >
                                  AppSecurity Assessment Complete
                                </h1>
                                <div
                                  style={{
                                    fontSize: "1.2rem",
                                    opacity: 0.9,
                                    marginBottom: "20px",
                                  }}
                                >
                                  Review your exam performance
                                </div>

                                {/* Level Badge */}
                                <div
                                  style={{
                                    display: "inline-block",
                                    padding: "12px 24px",
                                    borderRadius: "50px",
                                    fontSize: "1.1rem",
                                    fontWeight: "600",
                                    background:
                                      percentage >= 80
                                        ? "#10b981"
                                        : percentage >= 60
                                        ? "#f59e0b"
                                        : "#ef4444",
                                    boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
                                  }}
                                >
                                  {percentage >= 80
                                    ? "EXCELLENT"
                                    : percentage >= 60
                                    ? "GOOD JOB"
                                    : "IMPROVEMENT NEEDED"}
                                </div>
                              </div>

                              <Row className="g-4">
                                {/* Big Score Circle */}
                                <Col xs={12} md={4}>
                                  <div
                                    style={{
                                      background: "rgba(255,255,255,0.15)",
                                      backdropFilter: "blur(20px)",
                                      borderRadius: "50%",
                                      width: "200px",
                                      height: "200px",
                                      margin: "0 auto",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border:
                                        "4px solid rgba(255,255,255,0.3)",
                                      boxShadow:
                                        "inset 0 0 50px rgba(255,255,255,0.1)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "3.5rem",
                                        fontWeight: "bold",
                                        color: "white",
                                        textShadow:
                                          "0 4px 12px rgba(0,0,0,0.3)",
                                      }}
                                    >
                                      {percentage}%
                                    </div>
                                    <div
                                      style={{ fontSize: "1rem", opacity: 0.9 }}
                                    >
                                      Final Score
                                    </div>
                                  </div>
                                </Col>

                                {/* Stats Grid */}
                                <Col xs={12} md={8}>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        "repeat(auto-fit, minmax(150px, 1fr))",
                                      gap: "15px",
                                      marginBottom: "25px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        background: "#fff",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "2rem",
                                          fontWeight: "bold",
                                          color: "#60a5fa",
                                        }}
                                      >
                                        {totalQuestions}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.9rem",
                                          color: "gray",
                                        }}
                                      >
                                        Total Questions
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        background: "#fff",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "2rem",
                                          fontWeight: "bold",
                                          color: "#10b981",
                                        }}
                                      >
                                        {score}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.9rem",
                                          color: "gray",
                                        }}
                                      >
                                        Correct
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        background: "#fff",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "2rem",
                                          fontWeight: "bold",
                                          color: "#f59e0b",
                                        }}
                                      >
                                        {answeredQuestions - score}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.9rem",
                                          color: "gray",
                                        }}
                                      >
                                        Incorrect
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        background: "#fff",
                                        padding: "20px",
                                        borderRadius: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "2rem",
                                          fontWeight: "bold",
                                          color: "#777777",
                                        }}
                                      >
                                        {answeredQuestions}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.9rem",
                                          color: "gray",
                                        }}
                                      >
                                        Answered
                                      </div>
                                    </div>
                                  </div>

                                  {/* Performance Message */}
                                  <div
                                    style={{
                                      background: "#fff",
                                      padding: "25px",
                                      borderRadius: "16px",
                                      marginBottom: "25px",
                                    }}
                                  >
                                    <h4
                                      style={{
                                        marginBottom: "15px",
                                        fontWeight: "600",
                                        color: "#333333",
                                      }}
                                    >
                                      {percentage >= 80
                                        ? "Outstanding Performance!"
                                        : percentage >= 60
                                        ? "Good Work!"
                                        : "Keep Learning!"}
                                    </h4>
                                    <p
                                      style={{
                                        margin: 0,
                                        lineHeight: "1.6",
                                        color: "gray",
                                      }}
                                    >
                                      {percentage >= 80
                                        ? "Excellent application security readiness! You have strong command of OWASP risks, secrets management, and secure SDLC practices."
                                        : percentage >= 60
                                        ? "Good foundation. Review access control, secrets exposure, and secure SDLC concepts to strengthen your knowledge."
                                        : 'Revisit training slides on key concepts before retaking the assessment.'}
                                    </p>
                                  </div>

                                  {/* Action Buttons */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Button
                                      variant="primary"
                                      onClick={handleBackToIntro}
                                      style={{
                                        color: "white",
                                        padding: "12px 24px",
                                        borderRadius: "12px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      Back to Training
                                    </Button>

                                    {percentage >= 75 && (
                                      <Button
                                        variant="success"
                                        onClick={handleDownloadCertificate}
                                        style={{
                                          background: "#10b981",
                                          border: "none",
                                          color: "white",
                                          padding: "12px 24px",
                                          borderRadius: "12px",
                                          fontWeight: "600",
                                        }}
                                      >
                                        View Certificate
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
          <Col>
            <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
          </Col>
        </Col>
      </Row>

      {/* Certificate Preview Modal */}
      <Modal
        show={showCertificateModal}
        onHide={() => setShowCertificateModal(false)}
        size="xl"
        centered
      >
        <Modal.Header
          closeButton
          style={{ filter: "invert(1) brightness(0)", opacity: 1 }}
        >
          <Modal.Title>Application Security Certificate Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px" }}>
          {certificatePreviewUrl ? (
            <div
              style={{
                width: "100%",
                height: "600px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#525659",
              }}
            >
              <iframe
                src={certificatePreviewUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "8px",
                }}
                title="Certificate Preview"
              />
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "600px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
              }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading certificate...</span>
              </Spinner>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ justifyContent: "center", gap: "15px", padding: "20px" }}
        >
          <Button
            variant="primary"
            onClick={handleShareLinkedIn}
            style={{
              minWidth: "220px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "600",
              backgroundColor: "#0077b5",
              borderColor: "#0077b5",
            }}
          >
            <i className="fab fa-linkedin" style={{ marginRight: "8px" }}></i>
            Download and Share Certificate
          </Button>
        </Modal.Footer>
      </Modal>

      {/* LinkedIn Share Modal */}
      <Modal
        show={showLinkedInModal}
        onHide={() => setShowLinkedInModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "none", backgroundColor: "#f0f9ff" }}
        >
          <Modal.Title
            style={{ color: "#0077b5", fontWeight: "bold", fontSize: "1.5rem" }}
          >
            <i
              className="fab fa-linkedin"
              style={{ marginRight: "12px" }}
            ></i>
            Share on LinkedIn
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "30px" }}>
          <div style={{ textAlign: "center", marginBottom: "25px" }}>
            <div style={{ fontSize: "3rem", marginBottom: "15px" }}>✅</div>
            <h5
              style={{
                color: "#10b981",
                fontWeight: "600",
                marginBottom: "10px",
              }}
            >
              Certificate Downloaded as PNG!
            </h5>
            <p style={{ color: "#6b7280", fontSize: "0.95rem" }}>
              Check your Downloads folder for the certificate image
            </p>
          </div>

          <div
            style={{
              background: "#f8fafc",
              padding: "20px",
              borderRadius: "12px",
              border: "2px dashed #cbd5e1",
              marginBottom: "20px",
            }}
          >
            <h6
              style={{
                fontWeight: "600",
                marginBottom: "15px",
                color: "#1e293b",
                fontSize: "1.1rem",
              }}
            >
              Quick Steps to Share:
            </h6>
            <ol
              style={{
                lineHeight: "2",
                paddingLeft: "25px",
                marginBottom: "0",
                color: "#475569",
              }}
            >
              <li>
                Click <strong>"Download PNG & Open LinkedIn"</strong> button
                below
              </li>
              <li>Certificate will download as PNG image</li>
              <li>
                LinkedIn will open <strong>automatically</strong> in a new tab
              </li>
              <li>
                In LinkedIn, click <strong>"Add media"</strong> and select
                downloaded PNG
              </li>
              <li>
                Caption is pre-filled – mention @Servqual Security and click{" "}
                <strong>"Post"</strong>!
              </li>
            </ol>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "15px",
              borderRadius: "10px",
              color: "white",
              textAlign: "center",
            }}
          >
            <small>
              <strong>Pro Tip:</strong> PNG images look better on LinkedIn than
              PDFs!
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "none",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowLinkedInModal(false)}
            style={{ minWidth: "150px", fontWeight: "600" }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              console.log("🚀 Starting download & LinkedIn share...");
              await downloadCertificateAsPNG();
              setTimeout(() => {
                const linkedInCaption = encodeURIComponent(
                  "🎓 I'm pleased to share that I've completed the Application Security Fundamentals Certification on the SUSAN by ServQual \n\n" +
                    " https://susan.srql.com \n\n " +
                    " #AppSec #ApplicationSecurity #OWASP #SecureCoding #ServQual #SUSAN"
                );
                window.open(
                  `https://www.linkedin.com/feed/?shareActive=true&text=${linkedInCaption}`,
                  "_blank",
                  "noopener,noreferrer"
                );
                setShowLinkedInModal(false);
              }, 1500);
            }}
          >
            <i className="fab fa-linkedin" style={{ marginRight: "10px" }}></i>
            Download PNG & Share on LinkedIn
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden Certificate Canvas for PNG Generation */}
      {showCertificateCanvas && (
        <AppSecCertificateCanvas
          userData={userData}
          assessmentData={{ percentage, score, totalQuestions }}
          susanLogo={susanLogo}
          onRenderComplete={() => console.log("✅ Certificate rendered")}
        />
      )}
    </div>
  );
};

export default AppSecAssessmentModule;